/**
 * gift-sync.js  — v3 (clean architecture)
 *
 * GIFT DETECTION  — discount-based, zero hardcoded product IDs
 *   A cart item is a gift if:
 *     final_price === 0  AND  original_price > 0
 *     AND discounts[].title === GIFT_DISCOUNT_TITLE ("FREE")
 *   OR  item.properties._gift === "true"  (added by this module)
 *
 * GIFT RULES  — injected by Liquid via <script id="cd-config">
 *   giftRules: [{ triggerIds:[...], gifts:[{variantId, productId, title, url, image, compareAtPrice}] }]
 *   Liquid resolves all handles → IDs at render time. Zero JS deploys for new gifts.
 *
 * ROGUE FREE GUARD  — items that are free but not legitimate gifts are
 *   blocked at the fetch() intercept and purged on page load.
 */

if (window.CWGiftSyncInitialized) {
  console.warn('[GiftSync] gift-sync.js already initialized. Skipping duplicate include.');
} else {
  window.CWGiftSyncInitialized = true;

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     0.  Config  (injected by Liquid via <script id="cd-config">)
  ───────────────────────────────────────────────────────────── */
  const cfg = (function () {
    try { return JSON.parse(document.getElementById('cd-config')?.textContent || '{}'); }
    catch (_) { return {}; }
  })();

  const GIFT_DISCOUNT_TITLE = String(cfg.giftDiscountTitle || 'FREE').toUpperCase();
  const GIFT_RULES          = Array.isArray(cfg.giftRules) ? cfg.giftRules : [];

  /* ─────────────────────────────────────────────────────────────
     1.  Derived lookups  (built once from GIFT_RULES)
  ───────────────────────────────────────────────────────────── */
  const ALL_GIFT_VARIANT_IDS = new Set(
    GIFT_RULES.flatMap((r) => (r.gifts || []).map((g) => Number(g.variantId)))
  );
  const ALL_GIFT_PRODUCT_IDS = new Set(
    GIFT_RULES.flatMap((r) => (r.gifts || []).map((g) => Number(g.productId)))
  );
  /** variantId → gift metadata object */
  const GIFT_BY_VARIANT = new Map(
    GIFT_RULES.flatMap((r) => (r.gifts || []).map((g) => [Number(g.variantId), g]))
  );

  // Dynamic gift variant IDs from products marked with custom.is_gift = true
  // These are whitelisted so guards don't purge them even if not in GIFT_RULES
  const DYNAMIC_GIFT_VARIANT_IDS = new Set((cfg.dynamicGiftVariantIds || []).map((vid) => Number(vid)));

  // Global duplicate-gift aliases. This is not tied to any trigger product.
  // It only makes both Cat Toy products resolve to the same canonical gift.
  const GIFT_VARIANT_ALIASES = new Map([
    [47068573171932, 43628209078492]
  ]);

  const GIFT_PRODUCT_ALIASES = new Map([
    [9112325816540, 7922521440476]
  ]);

  function normalizeGiftAlias(gift) {
    if (!gift) return gift;

    const variantId = Number(gift.variantId || 0);
    const productId = Number(gift.productId || 0);
    const normalizedGift = { ...gift };

    if (GIFT_VARIANT_ALIASES.has(variantId)) {
      normalizedGift.variantId = GIFT_VARIANT_ALIASES.get(variantId);
    }

    if (GIFT_PRODUCT_ALIASES.has(productId)) {
      normalizedGift.productId = GIFT_PRODUCT_ALIASES.get(productId);
    }

    return normalizedGift;
  }

  function normalizeGiftRuleAliases(rule) {
    if (!rule) return rule;

    const gifts = Array.isArray(rule.gifts) ? rule.gifts : [];
    const normalizedGifts = [];
    const seenGiftKeys = new Set();

    gifts.forEach((gift) => {
      const normalizedGift = normalizeGiftAlias(gift);
      const key = Number(normalizedGift?.variantId || 0) || Number(normalizedGift?.productId || 0);

      if (!key || seenGiftKeys.has(key)) return;

      seenGiftKeys.add(key);
      normalizedGifts.push(normalizedGift);
    });

    return {
      ...rule,
      gifts: normalizedGifts
    };
  }

  function normalizeGiftRulesInPlace() {
    for (let i = 0; i < GIFT_RULES.length; i++) {
      GIFT_RULES[i] = normalizeGiftRuleAliases(GIFT_RULES[i]);
    }
  }

  function rebuildGiftLookups() {
    ALL_GIFT_VARIANT_IDS.clear();
    ALL_GIFT_PRODUCT_IDS.clear();
    GIFT_BY_VARIANT.clear();

    GIFT_RULES.forEach((rule) => {
      (rule.gifts || []).forEach((gift) => {
        const variantId = Number(gift.variantId);
        const productId = Number(gift.productId);

        if (variantId) {
          ALL_GIFT_VARIANT_IDS.add(variantId);
          GIFT_BY_VARIANT.set(variantId, gift);
        }

        if (productId) {
          ALL_GIFT_PRODUCT_IDS.add(productId);
        }
      });
    });

    DYNAMIC_GIFT_VARIANT_IDS.forEach((vid) => {
      const normalizedVid = GIFT_VARIANT_ALIASES.get(vid) || vid;
      if (normalizedVid) ALL_GIFT_VARIANT_IDS.add(normalizedVid);
    });
  }

  function normalizeRuleIds(ids) {
    return Array.from(
      new Set((ids || []).map((id) => Number(id)).filter(Boolean))
    );
  }

  function rulesShareAnyTrigger(a, b) {
    const aIds = new Set(normalizeRuleIds(a?.triggerIds));
    return normalizeRuleIds(b?.triggerIds).some((id) => aIds.has(id));
  }

  function sameGiftSet(a, b) {
    const aGifts = normalizeRuleIds((a?.gifts || []).map((gift) => gift.variantId)).sort().join(',');
    const bGifts = normalizeRuleIds((b?.gifts || []).map((gift) => gift.variantId)).sort().join(',');

    return aGifts === bGifts;
  }

  function registerPriorityGiftRule(rule, source) {
    if (!rule || !Array.isArray(rule.triggerIds) || !Array.isArray(rule.gifts) || !rule.gifts.length) {
      return false;
    }

    let normalizedRule = {
      ...rule,
      triggerIds: normalizeRuleIds(rule.triggerIds),
      gifts: (rule.gifts || []).filter((gift) => Number(gift.variantId)),
      _source: source || rule._source || 'metafield',
      _priority: true
    };

    normalizedRule = normalizeGiftRuleAliases(normalizedRule);

    if (!normalizedRule.triggerIds.length || !normalizedRule.gifts.length) {
      return false;
    }

    for (let i = GIFT_RULES.length - 1; i >= 0; i--) {
      const existingRule = GIFT_RULES[i];

      if (!rulesShareAnyTrigger(existingRule, normalizedRule)) continue;

      GIFT_RULES.splice(i, 1);
    }

    GIFT_RULES.push(normalizedRule);
    rebuildGiftLookups();

    console.log('[GiftSync] Priority gift rule registered:', {
      source: normalizedRule._source,
      triggerIds: normalizedRule.triggerIds,
      gifts: normalizedRule.gifts.map((gift) => gift.title || gift.variantId)
    });

    return true;
  }

  normalizeGiftRulesInPlace();
  rebuildGiftLookups();

  /* ─────────────────────────────────────────────────────────────
     2.  Utilities
  ───────────────────────────────────────────────────────────── */
  const H = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function money(cents) {
    const v = Number(cents || 0);
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(v, window.Shopify.money_format || '${{amount}}');
    return '$' + (v / 100).toFixed(2);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: H }).then((r) => r.json());
  }

  /* ─────────────────────────────────────────────────────────────
     3.  Gift detection  — discount-based, zero hardcoded pids
  ───────────────────────────────────────────────────────────── */
  function isGiftItem(item) {
    // Primary: Shopify's own automatic discount is the source of truth
    if (
      Number(item.final_price) === 0 &&
      Number(item.original_price) > 0 &&
      (item.discounts || []).some((d) => String(d.title || '').toUpperCase() === GIFT_DISCOUNT_TITLE)
    ) return true;

    // Secondary: tagged by this module on add
    if (item?.properties?._gift === 'true') return true;

    // Tertiary: in derived sets (covers items not yet discounted by Shopify)
    if (ALL_GIFT_VARIANT_IDS.has(Number(item.variant_id))) return true;
    if (ALL_GIFT_PRODUCT_IDS.has(Number(item.product_id))) return true;

    return false;
  }

  /* ─────────────────────────────────────────────────────────────
     4.  Gift resolution
  ───────────────────────────────────────────────────────────── */
  // BND SKUs are bundles that already contain their items: they never trigger free gifts (2026-10-08)
  function isBundleSku(item) {
    return /^BND/i.test(String((item && item.sku) || ''));
  }

  function getTriggerIds(cart) {
    const ids = new Set();
    for (const item of cart.items || []) {
      if (!isGiftItem(item) && !isBundleSku(item)) {
        ids.add(Number(item.product_id));
        ids.add(Number(item.variant_id));
      }
    }
    return ids;
  }

  function getTriggerQuantities(cart) {
    const quantities = new Map();

    for (const item of cart.items || []) {
      if (isGiftItem(item) || isBundleSku(item)) continue;

      const qty = Math.max(1, Number(item.quantity || 1));
      const productId = Number(item.product_id || 0);
      const variantId = Number(item.variant_id || 0);

      if (productId) quantities.set(productId, (quantities.get(productId) || 0) + qty);
      if (variantId) quantities.set(variantId, (quantities.get(variantId) || 0) + qty);
    }

    return quantities;
  }

  function getRuleTargetQuantity(rule, triggerQuantities) {
    let target = 0;

    for (const id of rule.triggerIds || []) {
      target = Math.max(target, Number(triggerQuantities.get(Number(id)) || 0));
    }

    return Math.max(target || 1, 1);
  }

  /** Returns Map<variantId, giftObject> of all gifts that should be in cart */
  function resolveRequiredGifts(triggerIds, triggerQuantities = new Map()) {
    const required = new Map();

    for (const rule of GIFT_RULES) {
      if (!(rule.triggerIds || []).some((id) => triggerIds.has(Number(id)))) continue;

      const targetQuantity = getRuleTargetQuantity(rule, triggerQuantities);

      for (const gift of rule.gifts || []) {
        const vid = Number(gift.variantId);
        if (!vid) continue;

        const current = required.get(vid);
        required.set(vid, {
          ...gift,
          targetQuantity: Math.max(Number(current?.targetQuantity || 0), targetQuantity)
        });
      }
    }

    return required;
  }

  /* ─────────────────────────────────────────────────────────────
     5.  Cart mutation helpers
  ───────────────────────────────────────────────────────────── */
  async function addGiftsBatch(giftObjects, liveCart) {
    const presentVariants = new Set((liveCart.items || []).map((i) => Number(i.variant_id)));
    const seen  = new Set();
    const items = [];

    for (const gift of giftObjects) {
      const vid = Number(gift.variantId);
      if (!vid || seen.has(vid) || presentVariants.has(vid)) continue;

      seen.add(vid);

      items.push({
        id: vid,
        quantity: Math.max(Number(gift.targetQuantity || 1), 1),
        properties: { _gift: 'true' }
      });
    }

    if (!items.length) return null;

    console.log('[GiftSync] Adding gifts:', items.map((i) => `${i.id} x${i.quantity}`));

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: H,
      body: JSON.stringify({ items })
    });

    return res.json();
  }

  /* ─────────────────────────────────────────────────────────────
     6.  DOM helpers
  ───────────────────────────────────────────────────────────── */
  function fadeRemove(el) {
    if (!el) return;
    el.style.transition = 'opacity .18s ease, transform .18s ease';
    el.style.opacity    = '0';
    el.style.transform  = 'translateX(8px)';
    setTimeout(() => el.remove(), 200);
  }

  function hideGiftsByVariantIds(variantIds) {
    const list = document.getElementById('cd-items');
    if (!list) return;
    variantIds.forEach((vid) => {
      list.querySelectorAll(`[data-gift-item="true"][data-variant="${vid}"]`).forEach(fadeRemove);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     7.  Gift DOM rendering  (gift-sync owns all gift <li>s)
  ───────────────────────────────────────────────────────────── */
  function buildRealGiftLi(item) {
    const vid  = Number(item.variant_id);
    const meta = GIFT_BY_VARIANT.get(vid) || {};
    const title = item.product_title || meta.title || 'Free Gift';
    const img   = item.image         || meta.image || '';
    const cmp   = Number(meta.compareAtPrice || 0);

    const li = document.createElement('li');
    li.className        = 'cd-item cd-item--gift';
    li.dataset.key      = item.key;
    li.dataset.variant  = item.variant_id;
    li.dataset.giftItem = 'true';

    li.innerHTML = `
      <span class="cd-item-thumb" aria-hidden="true" style="pointer-events:none;cursor:default;">
        ${img ? `<img src="${esc(img)}" alt="${esc(title)}" width="80" height="80" loading="lazy">` : ''}
      </span>
      <div class="cd-item-info">
        <span class="cd-item-name" style="pointer-events:none;cursor:default;">${esc(title)}</span>
        <span class="cd-badge-free">FREE</span>
        <div class="cd-item-foot">
          <div class="cd-qty-widget cd-qty-widget--gift is-locked" aria-label="Quantity">
            <button type="button" class="cd-qbtn" disabled aria-disabled="true" tabindex="-1" aria-label="Gift quantity cannot be decreased">
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                <path d="M1 1H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
            <span class="cd-qty-val" aria-label="Quantity">${Math.max(Number(item.quantity || 1), 1)}</span>
            <button type="button" class="cd-qbtn" disabled aria-disabled="true" tabindex="-1" aria-label="Gift quantity cannot be increased">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M5 1V9M1 5H9" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="cd-price-block">
            <span class="cd-p cd-p-sale">${money(0)}</span>
            ${cmp ? `<span class="cd-p cd-p-was">${money(cmp)}</span>` : ''}
          </div>
        </div>
      </div>
      <button type="button" class="cd-del" data-remove data-key="${esc(item.key)}" aria-label="Remove gift">
        <svg width="13" height="15" viewBox="0 0 13 15" fill="none" aria-hidden="true">
          <path d="M1 3.5H12M4.5 3.5V2H8.5V3.5M2 3.5L2.6 13H10.4L11 3.5"
            stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;
    return li;
  }

  function renderGiftsInDrawer(cart) {
    const list = document.getElementById('cd-items');
    if (!list) return;
    list.querySelectorAll('[data-gift-item="true"]').forEach((el) => el.remove());
    (cart.items || []).filter(isGiftItem).forEach((item) => list.appendChild(buildRealGiftLi(item)));
  }

  /* ─────────────────────────────────────────────────────────────
     8.  Drawer notification  (self-loop guarded)
  ───────────────────────────────────────────────────────────── */
  let _isSelfNotify = false;

  function notifyDrawer(cart) {
    _isSelfNotify = true;
    setTimeout(() => { _isSelfNotify = false; }, 1200);
    if (window.CartDrawer?.refresh) { window.CartDrawer.refresh(cart); return; }
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
  }

  /* ─────────────────────────────────────────────────────────────
     9.  Sync engine  — single-pass, no early returns
  ───────────────────────────────────────────────────────────── */
  let _syncRunning = false;
  let _syncPending = false;
  let _syncPromise = null;

  function scheduleSyncGifts(cartHint) {
    if (_syncRunning) {
      _syncPending = true;
      return _syncPromise || Promise.resolve();
    }

    _syncPromise = _runSyncGifts(cartHint).finally(() => {
      _syncPromise = null;
    });

    return _syncPromise;
  }

  async function _runSyncGifts(cartHint) {
    _syncRunning = true;
    _syncPending = false;

    try {
      const cart = cartHint || await fetchCart();
      window._giftSyncCartCache = cart;

      const triggerIds = getTriggerIds(cart);
      const triggerQuantities = getTriggerQuantities(cart);
      const required = resolveRequiredGifts(triggerIds, triggerQuantities); // Map<vid, giftObj>

      // ── Phase 1: classify every gift in the cart ────────────────────────
      // correctGifts: vid → surviving item (right variant, first occurrence, qty OK)
      // allUpdates:   key → target qty     (0 = remove, 1 = force qty to 1)
      const correctGifts = new Map();
      const allUpdates   = {};

      for (const item of cart.items || []) {
        if (!isGiftItem(item)) continue;

        const vid = Number(item.variant_id);

        // Unmanaged free item (not in our rules, not tagged by us) → remove
        const isManagedGift = ALL_GIFT_VARIANT_IDS.has(vid) || item?.properties?._gift === 'true';
        if (!isManagedGift && !required.has(vid)) {
          console.warn('[GiftSync] Unmanaged free item — removing:', item.product_title, 'vid:', vid);
          allUpdates[item.key] = 0;
          continue;
        }

        if (!correctGifts.has(vid)) {
          correctGifts.set(vid, item);

          if (required.has(vid)) {
            const targetQty = Math.max(Number(required.get(vid)?.targetQuantity || 1), 1);
            const currentQty = Number(item.quantity || 1);

            if (currentQty !== targetQty) {
              console.warn('[GiftSync] Gift qty correction:', item.product_title, 'current:', currentQty, 'target:', targetQty);
              allUpdates[item.key] = targetQty;
            }
          } else if (Number(item.quantity) > 2) {
            allUpdates[item.key] = 2;
          }
        } else {
          console.warn('[GiftSync] Duplicate gift line — removing:', item.product_title);
          allUpdates[item.key] = 0;
        }
      }

      // A fountain in the cart that no gift rule knows (e.g. hero PDPs whose gifts come from the offer box): its gifts are
      // not "required" here, but they are not orphans either, so keep them while that fountain is in the cart. Once it is
      // removed they are orphans again and get cleaned up. (Weekly QA 2026-10-10, F1: opening the cart on non-PDP pages
      // stripped the gifts of 2.2L / Colored Wireless 2.0.)
      const knownTriggers = new Set();
      for (const rule of GIFT_RULES) for (const id of (rule.triggerIds || [])) knownTriggers.add(Number(id));
      const unknownFountainInCart = (cart.items || []).some(i =>
        !isGiftItem(i) && !isBundleSku(i) && /^WF-/i.test(String(i.sku || '')) &&
        !knownTriggers.has(Number(i.variant_id)) && !knownTriggers.has(Number(i.product_id)));

      // Orphans: in correctGifts but not required → remove
      for (const [vid, item] of Array.from(correctGifts)) {
        if (!required.has(vid) && unknownFountainInCart) {
          console.log('[GiftSync] Kept (fountain without a cart rule is in the cart):', item.product_title);
          continue;
        }
        if (!required.has(vid)) {
          console.log('[GiftSync] Orphan — removing:', item.product_title);
          allUpdates[item.key] = 0;
          correctGifts.delete(vid);
        }
      }

      // ── Phase 2: apply all corrections in one update.js call ─────────────
      if (Object.keys(allUpdates).length) {
        console.log('[GiftSync] Corrections:', allUpdates);
        await fetch('/cart/update.js', { method: 'POST', headers: H, body: JSON.stringify({ updates: allUpdates }) });
        // Small delay to let Shopify reflect the update before we fetch again
        await new Promise(r => setTimeout(r, 300));
      }

      // ── Phase 3: fetch post-correction cart, then add any missing gifts ──
      // Always fetch fresh here — never use a stale hint after corrections.
      const postFixCart = await fetchCart();
      window._giftSyncCartCache = postFixCart;

      // Rebuild correctGifts from the fresh cart to know what's actually present
      const presentGiftVids = new Set(
        (postFixCart.items || [])
          .filter(i => ALL_GIFT_VARIANT_IDS.has(Number(i.variant_id)) || i?.properties?._gift === 'true')
          .map(i => Number(i.variant_id))
      );
      const toAdd = [...required.values()].filter(g => !presentGiftVids.has(Number(g.variantId)));

      if (toAdd.length) {
        console.log('[GiftSync] Adding missing gifts:', toAdd.map(g => g.variantId));
        await addGiftsBatch(toAdd, postFixCart);
        // Small delay before final fetch
        await new Promise(r => setTimeout(r, 200));
      }

      // ── Phase 4: final fetch → notify drawer ────────────────────────────
      const finalCart = await fetchCart();
      window._giftSyncCartCache = finalCart;
      _lastCartFingerprint = cartFingerprint(finalCart);
      notifyDrawer(finalCart);

    } catch (err) {
      console.error('[GiftSync] Sync error:', err);
    } finally {
      _syncRunning = false;

      if (_syncPending) {
        _syncPending = false;
        return _runSyncGifts();
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────
     10. Atomic parent → child removal interceptor
  ───────────────────────────────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-remove]');
    if (!removeBtn) return;

    const li  = removeBtn.closest('.cd-item');
    const key = removeBtn.dataset.key || '';
    if (!li || !key) return;

    const cachedCart = window._giftSyncCartCache;
    if (!cachedCart) return;

    const removedItem = (cachedCart.items || []).find((i) => i.key === key);
    if (!removedItem) return;

    // If user manually removes a gift, remove it directly — no parent cascade needed
    if (isGiftItem(removedItem)) {
      e.stopImmediatePropagation();
      fadeRemove(li);
      _isSelfNotify = true;
      fetch('/cart/update.js', { method: 'POST', headers: H, body: JSON.stringify({ updates: { [key]: 0 } }) })
        .then((r) => r.json())
        .then((updatedCart) => {
          window._giftSyncCartCache = updatedCart;
          _lastCartFingerprint = cartFingerprint(updatedCart);
          notifyDrawer(updatedCart);
        })
        .catch(() => fetchCart().then((c) => { window._giftSyncCartCache = c; notifyDrawer(c); }))
        .finally(() => { setTimeout(() => { _isSelfNotify = false; }, 1200); });
      return;
    }

    const remainingIds = new Set();
    for (const item of cachedCart.items || []) {
      if (item.key === key || isGiftItem(item)) continue;
      remainingIds.add(Number(item.product_id));
      remainingIds.add(Number(item.variant_id));
    }

    const stillRequired = resolveRequiredGifts(remainingIds, new Map());
    const orphanKeys = [];
    const orphanVids = [];

    for (const item of cachedCart.items || []) {
      if (!isGiftItem(item)) continue;
      const vid = Number(item.variant_id);
      if (!stillRequired.has(vid)) {
        orphanKeys.push(item.key);
        if (!orphanVids.includes(vid)) orphanVids.push(vid);
      }
    }

    if (!orphanKeys.length) return;

    e.stopImmediatePropagation();
    fadeRemove(li);
    hideGiftsByVariantIds(orphanVids);

    const updates = { [key]: 0 };
    orphanKeys.forEach((k) => { updates[k] = 0; });
    console.log('[GiftSync] Atomic parent→child remove:', Object.keys(updates));

    _isSelfNotify = true;
    fetch('/cart/update.js', { method: 'POST', headers: H, body: JSON.stringify({ updates }) })
      .then((r) => r.json())
      .then((updatedCart) => {
        window._giftSyncCartCache = updatedCart;
        _lastCartFingerprint = cartFingerprint(updatedCart);
        notifyDrawer(updatedCart);
      })
      .catch(() => fetchCart().then((c) => { window._giftSyncCartCache = c; notifyDrawer(c); }))
      .finally(() => { setTimeout(() => { _isSelfNotify = false; }, 1200); });
  }, true);

  /* ─────────────────────────────────────────────────────────────
     11. Rogue free-item guard
  ───────────────────────────────────────────────────────────── */
  /**
   * isRogueFreeItem uses EXPLICIT set membership only — NOT discount-based detection.
   * Reason: Rebuy adds gifts with discount title "FREE" and final_price=0, so
   * isGiftItem() (which is discount-based) would return true for Rebuy items too,
   * causing isRogueFreeItem to return false and letting Rebuy gifts slip through.
   * By checking ALL_GIFT_VARIANT_IDS directly, only our managed variants are exempt.
   */
  function isRogueFreeItem(item) {
    const vid      = Number(item.variant_id);
    const managed  = ALL_GIFT_VARIANT_IDS.has(vid) || item?.properties?._gift === 'true';
    return Number(item.final_price) === 0 && Number(item.original_price) > 0 && !managed;
  }

  async function purgeRogueFreeItems(cart) {
    const updates = {};
    for (const item of cart.items || []) {
      if (isRogueFreeItem(item)) {
        console.warn('[GiftSync] Purging rogue free item:', item.product_title);
        updates[item.key] = 0;
      }
    }
    if (!Object.keys(updates).length) return null;
    const res = await fetch('/cart/update.js', { method: 'POST', headers: H, body: JSON.stringify({ updates }) });
    return res.json();
  }

  (function installFetchIntercept() {
    const _origFetch = window.fetch.bind(window);

    window.fetch = async function (input, init) {
      const url = typeof input === 'string' ? input : (input?.url || '');
      const isCartAdd = url.includes('/cart/add');
      const isCartChangeOrUpdate = url.includes('/cart/change') || url.includes('/cart/update');

      if (!isCartAdd) {
        const response = await _origFetch(input, init);

        if (isCartChangeOrUpdate && !_syncRunning) {
          try {
            response.clone().json().then((cart) => {
              window.setTimeout(() => scheduleSyncGifts(cart), 0);
            }).catch(() => {
              window.setTimeout(() => scheduleSyncGifts(), 0);
            });
          } catch (_) {
            window.setTimeout(() => scheduleSyncGifts(), 0);
          }
        }

        return response;
      }

      const body = init?.body;

      // JSON body
      if (typeof body === 'string') {
        let parsed;
        try { parsed = JSON.parse(body); } catch (_) { return _origFetch(input, init); }
        const rawItems = parsed.items
          ? parsed.items
          : parsed.id ? [{ id: parsed.id, quantity: parsed.quantity || 1, properties: parsed.properties || {} }]
          : null;

        if (rawItems) {
          const checked = await Promise.all(rawItems.map(async (item) => {
            const vid = Number(item.id || 0);
            // Our managed gift variants → always allow through
            if (ALL_GIFT_VARIANT_IDS.has(vid) || item.properties?._gift === 'true') return item;
            // Any non-managed variant that resolves to free at cart level is rogue.
            // We detect via isRogueFreeItem on cart items, but at add-time we must check
            // the Shopify product price. However, Rebuy gifts have list price > 0 but
            // are discounted to 0 at cart level — so price check won't block them.
            // Instead: block any non-managed free item that is NOT _gift tagged.
            // For Rebuy items specifically: they come via POST without _gift property.
            // Since we can't check cart-level price at add-time, we use a different signal:
            // if the item has no _gift property and is not our variant, check if the
            // product it belongs to is one of our GIFT product IDs (different variant).
            // If it is, it's a Rebuy duplicate of our gift product → block it.
            const ALL_GIFT_PRODUCT_IDS_SET = ALL_GIFT_PRODUCT_IDS; // already defined in scope
            // We can't easily check product_id from just variant_id at intercept time without
            // a fetch. So we fall back to: block if the variant's product is a known gift product.
            // This requires fetching /variants/{vid}.js anyway.
            try {
              const r = await _origFetch(`/variants/${vid}.js`, { headers: H });
              if (r.ok) {
                const v = await r.json();
                // Block if: product-level price is 0 and compare_at_price > 0 (a free product)
                if (Number(v.price) === 0 && Number(v.compare_at_price) > 0) {
                  console.warn('[GiftSync] BLOCKED rogue free variant (zero-priced product):', vid);
                  return null;
                }
                // Block if: this variant belongs to a product that IS one of our gift products
                // but the variant is NOT our canonical variant (i.e. Rebuy is adding a different variant)
                if (ALL_GIFT_PRODUCT_IDS_SET.has(Number(v.product_id)) && !ALL_GIFT_VARIANT_IDS.has(vid)) {
                  console.warn('[GiftSync] BLOCKED non-canonical variant of a gift product:', vid, 'pid:', v.product_id);
                  return null;
                }
              }
            } catch (_) { /* fail open */ }
            return item;
          }));

          const allowed = checked.filter(Boolean);
          if (!allowed.length) {
            return new Response(JSON.stringify({ status: 422, message: 'Item not allowed.' }), { status: 422, headers: { 'Content-Type': 'application/json' } });
          }
          if (allowed.length < rawItems.length) {
            const newPayload = parsed.items
              ? { ...parsed, items: allowed }
              : { id: allowed[0].id, quantity: allowed[0].quantity, properties: allowed[0].properties };
            init = { ...init, body: JSON.stringify(newPayload) };
          }
        }
      }

      // FormData body
      if (body instanceof FormData) {
        const vid = Number(body.get('id') || 0);
        if (vid && !ALL_GIFT_VARIANT_IDS.has(vid) && body.get('properties[_gift]') !== 'true') {
          try {
            const r = await _origFetch(`/variants/${vid}.js`, { headers: H });
            if (r.ok) {
              const v = await r.json();
              if (Number(v.price) === 0 && Number(v.compare_at_price) > 0) {
                console.warn('[GiftSync] BLOCKED rogue free FormData variant:', vid);
                return new Response(JSON.stringify({ status: 422, message: 'Item not allowed.' }), { status: 422, headers: { 'Content-Type': 'application/json' } });
              }
            }
          } catch (_) { /* fail open */ }
        }
      }

      return _origFetch(input, init);
    };
  })();

  function installCheckoutSyncGuard() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('#CartDrawerForm');
      if (!form) return;

      if (form.dataset.giftSyncReady === 'true') {
        delete form.dataset.giftSyncReady;
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      const submitter = e.submitter || form.querySelector('[name="checkout"], [data-checkout], button[type="submit"], input[type="submit"]');

      if (submitter) submitter.disabled = true;

      scheduleSyncGifts().finally(() => {
        if (submitter) submitter.disabled = false;

        form.dataset.giftSyncReady = 'true';

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter || undefined);
        } else {
          form.submit();
        }

        window.setTimeout(() => {
          delete form.dataset.giftSyncReady;
        }, 500);
      });
    }, true);

    document.addEventListener('click', (e) => {
      const checkoutLink = e.target.closest('#CartDrawer a[href*="/checkout"]');
      if (!checkoutLink || checkoutLink.dataset.giftSyncReady === 'true') return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const href = checkoutLink.href;
      checkoutLink.style.pointerEvents = 'none';
      checkoutLink.style.opacity = '0.7';

      scheduleSyncGifts().finally(() => {
        checkoutLink.dataset.giftSyncReady = 'true';
        window.location.href = href;
      });
    }, true);
  }

  installCheckoutSyncGuard();

  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('#CartDrawer [data-qty-change]');
    if (!qtyBtn) return;

    const li = qtyBtn.closest('.cd-item');
    if (!li || li.dataset.giftItem === 'true') return;

    window.setTimeout(() => scheduleSyncGifts(), 700);
  }, true);


  /* ─────────────────────────────────────────────────────────────
     12. cart:updated listener
  ───────────────────────────────────────────────────────────── */
  let _lastCartFingerprint = '';
  let _debounceTimer       = null;

  function cartFingerprint(cart) {
    return (cart?.items || []).map((i) => `${i.key}:${i.quantity}`).sort().join('|');
  }

  document.addEventListener('cart:updated', async (e) => {
    if (_isSelfNotify) return;

    let cart = e.detail?.cart;
    if (cart) {
      if ((cart.items || []).some(isRogueFreeItem)) {
        const purged = await purgeRogueFreeItems(cart);
        if (purged) cart = purged;
      }
      window._giftSyncCartCache = cart;
      const fp = cartFingerprint(cart);
      if (fp && fp === _lastCartFingerprint) return;
      _lastCartFingerprint = fp;
    }

    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(() => scheduleSyncGifts(cart || null), 0);
  });

  /* ─────────────────────────────────────────────────────────────
     13. Page-load sync
  ───────────────────────────────────────────────────────────── */
  async function initSync() {
    // Pick up any rule queued by product-form-atc.js before we initialized.
    // A pending rule comes from metafields and must override any static/hardcoded
    // rule that shares the same trigger product/variant.
    if (window._pendingGiftRule) {
      const rule = window._pendingGiftRule;
      window._pendingGiftRule = null;

      registerPriorityGiftRule(rule, 'metafield-pending');

      console.log('[GiftSync] Picked up pending priority gift rule on init:',
        rule.gifts?.map(g => g.title).join(', '));
    }

    const cart       = await fetchCart();
    const purgedCart = await purgeRogueFreeItems(cart);
    const freshCart  = purgedCart || cart;
    window._giftSyncCartCache = freshCart;
    _lastCartFingerprint = cartFingerprint(freshCart);
    scheduleSyncGifts(freshCart);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSync);
  } else {
    initSync();
  }

  /* ─────────────────────────────────────────────────────────────
     14. Public API
  ───────────────────────────────────────────────────────────── */
  window.KittySyncGifts = scheduleSyncGifts;
  window.GiftSync = {
    renderInDrawer: renderGiftsInDrawer,
    isGift:         isGiftItem,

    /** True while a gift sync is running or queued (used by the drawer checkout guard). */
    isBusy: () => _syncRunning || _syncPending,

    /** True when the cart's gift lines exactly match the gifts the current rules require (alias-aware). */
    isInSync(cart) {
      const alias = (vid) => (GIFT_VARIANT_ALIASES.has(vid) ? GIFT_VARIANT_ALIASES.get(vid) : vid);
      const required = new Map();
      resolveRequiredGifts(getTriggerIds(cart), getTriggerQuantities(cart)).forEach((gift, vid) => {
        required.set(alias(Number(vid)), Math.max(Number(gift?.targetQuantity || 1), 1));
      });
      const actual = new Map();
      for (const item of cart.items || []) {
        if (!isGiftItem(item)) continue;
        const vid = alias(Number(item.variant_id));
        if (!required.has(vid) && DYNAMIC_GIFT_VARIANT_IDS.has(vid)) continue;
        actual.set(vid, (actual.get(vid) || 0) + Number(item.quantity || 0));
      }
      if (actual.size !== required.size) return false;
      for (const [vid, qty] of required) if (actual.get(vid) !== qty) return false;
      return true;
    },

    /**
     * registerRule — dynamically add a gift rule at runtime.
     * Called by product-form-atc.js when the page product has
     * a metafield-based gift rule (custom.gift_handles).
     * Deduplicates: if a rule for the same triggerIds already exists
     * in the static GIFT_RULES, it is not added again.
     * After registration, a sync is scheduled to add any newly required gifts.
     */
    registerRule(rule) {
      if (!rule || !Array.isArray(rule.triggerIds) || !Array.isArray(rule.gifts)) return;

      let normalizedIncoming = {
        ...rule,
        triggerIds: normalizeRuleIds(rule.triggerIds),
        gifts: (rule.gifts || []).filter((gift) => Number(gift.variantId))
      };

      normalizedIncoming = normalizeGiftRuleAliases(normalizedIncoming);

      const alreadyExists = GIFT_RULES.some((existingRule) => {
        const sameTriggers = normalizeRuleIds(existingRule.triggerIds).sort().join(',') === normalizedIncoming.triggerIds.slice().sort().join(',');
        return sameTriggers && sameGiftSet(existingRule, normalizedIncoming);
      });

      if (alreadyExists) {
        console.log('[GiftSync] Priority rule already registered, skipping:', normalizedIncoming.triggerIds.slice(0, 3));
        return;
      }

      // Runtime rules come from metafields and must override static/hardcoded mappings
      // for the same trigger product/variant.
      const registered = registerPriorityGiftRule(normalizedIncoming, 'metafield-runtime');

      if (!registered) return;

      // Re-sync so old hardcoded gifts become orphans and the metafield gifts are added.
      scheduleSyncGifts();
    },
  };

  // Signal to product-form-atc.js that GiftSync is ready for rule registration
  document.dispatchEvent(new CustomEvent('giftsync:ready', { bubbles: true }));

})();
}